export const profile = {
  name: "Geetanshu Kumboj",
  title: "AI & Automation Engineer",
  certBadge: "Microsoft Certified · Azure AI Engineer",
  certBadgeHref: "#cert-azure-ai-engineer",
  tagline: {
    before: "Automate the",
    accent: "routine.",
    after: "Amplify what matters.",
  },
  subtagline:
    "3+ years building RAG agents, React apps, and RPA at enterprise scale — helping organizations automate faster and deliver measurable impact.",
  email: "kambojgeetanshu@gmail.com",
  phone: "778-538-1819",
  location: "British Columbia, Canada",
  linkedin: "https://www.linkedin.com/in/geetanshu-kumboj-03b39a1a5",
  resumeUrl: "/resume.pdf",
  photo: "/profile-photo.jpeg",
};

export const impact = [
  { value: "80%", label: "of HR inquiries automated with RAG agent" },
  { value: "48 hrs", label: "reclaimed per week through orchestration" },
  { value: "Legacy → AI", label: "approvals modernized with RPA + agents" },
  { value: "20K+", label: "users served across deployed systems" },
  { value: "99.9%", label: "uptime on mission-critical workflows" },
];

export const projects = [
  {
    id: "intranet",
    index: "01",
    name: "The Intranet",
    subtitle: "LinkedIn-style feed for an entire college.",
    confidential: true,
    image:
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    video: "https://www.loom.com/embed/5d85f8d9a616435c8a473f968ff25960",
    metrics: [
      { v: "2,000+", l: "active employees" },
      { v: "7→1", l: "legacy apps consolidated" },
    ],
    problem:
      "Employees were drowning in scattered legacy portals, each with its own login and its own version of the truth. Internal communication was a wall of email blasts.",
    approach:
      "Prototyped the experience in Lovable for fast stakeholder buy-in, then deployed the React frontend to AWS S3 and stood up a Payload (headless) CMS so the comms team could publish without ever filing a ticket.",
    outcome:
      "A real-time activity feed, a centralized Employee Digital Toolbox, and a single executive approval surface — replacing seven legacy applications with one calm, fast interface.",
    stack: ["React", "Payload CMS", "AWS S3", "RPA", "AI Agents"],
  },
  {
    id: "rag-chatbot",
    index: "02",
    name: "The RAG That Knows Who's Asking",
    subtitle: "Not your average chatbot. Identity-aware HR — answers that change with role, department, and clearance.",
    confidential: true,
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    metrics: [
      { v: "80%", l: "of HR inquiries deflected" },
      { v: "Entra ID", l: "secured at the identity layer" },
    ],
    problem:
      "Most RAG demos answer the same question the same way for everyone — and that's exactly what kills them in HR. Policy depends on role, department, and pay grade. A generic chatbot leaking the wrong answer to the wrong employee isn't a UX bug; it's a compliance incident.",
    approach:
      "Not a single retrieval pipeline — a routing one. The agent first authenticates the user via Entra ID, then makes a secure call to the ERP for their profile. From there it hands the query to a specialist sub-agent with retrieval scoped only to the documents that role is permitted to see. Different employee, different agent, different corpus.",
    outcome:
      "An assistant that feels personal but stays compliant — automating roughly 80% of HR's inbound questions while keeping every document access governed by identity, not vibes.",
    stack: ["Azure AI Foundry", "Python", "RAG", "Microsoft Entra ID", "Multi-Agent", "ERP Integration"],
  },
  {
    id: "invoice",
    index: "03",
    name: "Invoice Processing, Reimagined",
    subtitle: "From inbox to approval, untouched by humans.",
    confidential: true,
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    metrics: [
      { v: "Email→Approved", l: "zero manual handoffs" },
      { v: "IDP + RPA", l: "hybrid orchestration" },
    ],
    problem:
      "Finance teams burn enormous hours on the same loop: read invoice, key fields into a form, route for approval, repeat. It is the single biggest time sink in most back offices.",
    approach:
      "An incoming Outlook invoice fires a Power Automate flow that asks the reviewer for a one-tap Teams approval. On confirmation, the attachment lands in Blob Storage. Event Grid wakes an Azure Function that calls Document Intelligence, runs the calculation layer, and then either triggers a UiPath RPA bot or a modern API to file the invoice into the correct approval ladder.",
    outcome:
      "A workflow that turns a multi-step manual chore into a notification, a tap, and a fully governed downstream pipeline — extensible to any tool the org adopts next.",
    stack: ["Power Automate", "Azure Document Intelligence", "Azure Functions", "Event Grid", "UiPath"],
  },
  {
    id: "approvals",
    index: "04",
    name: "Approvals, At a Glance",
    subtitle: "An Intranet widget that returns 1–2 hours per manager, every week.",
    confidential: true,
    image:
      "https://images.unsplash.com/photo-1554224154-26032ffc0d07?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    metrics: [
      { v: "1–2 hrs", l: "saved per manager weekly" },
      { v: "Zero", l: "approval requests forgotten" },
    ],
    problem:
      "Leaders and managers waste hours combing inboxes for stuck approvals — leave, expenses, time-off — while their teams chase them down for the same thing.",
    approach:
      "Built an Approvals widget on the Intranet wired to a Microsoft Copilot agent. On load, the agent scans the manager’s mailbox and surfaces every pending request in one composed view.",
    outcome:
      "Decisions happen in seconds, not on Friday afternoon. Managers reclaim 1–2 hours each week; their teams stop waiting.",
    stack: ["Microsoft Copilot", "Microsoft Graph", "React", "Power Platform"],
  },
  {
    id: "telephony",
    index: "05",
    name: "Telephony AI for Small Business",
    subtitle: "The receptionist that never misses a call worth taking.",
    confidential: true,
    image:
      "https://images.unsplash.com/photo-1634648852208-fc7e55a15751?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBtZXRhbGxpYyUyMHRleHR1cmV8ZW58MHx8fHwxNzc5NDcyMTYyfDA&ixlib=rb-4.1.0&q=85",
    metrics: [
      { v: "24/7", l: "answered, even after hours" },
      { v: "Auto-booked", l: "into Teams calendars" },
    ],
    problem:
      "A missed phone call at a small business is a missed customer. After-hours calls, simple FAQs, and appointment requests routinely fall through the cracks.",
    approach:
      "Deployed a voice AI agent backed by a per-business knowledge base, with a secondary scheduling agent that checks live availability on the team’s Microsoft Teams calendar and confirms the booking on the call.",
    outcome:
      "Every call is answered, common questions resolved instantly, and qualified appointments land on the calendar before the customer hangs up.",
    stack: ["Voice AI", "RAG", "Microsoft Graph", "Teams Calendar"],
  },
  {
    id: "fabric",
    index: "06",
    name: "Fabric Agents, Not Dashboards",
    subtitle: "Why scroll through Power BI when you can ask?",
    confidential: true,
    image:
      "https://images.unsplash.com/photo-1543286386-713bdd548da4?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    metrics: [
      { v: "Conversational", l: "executive data access" },
      { v: "Zero reports", l: "to hunt through" },
    ],
    problem:
      "Executives don’t want to learn which of fifteen Power BI tabs holds the answer. They want the answer. Static dashboards are a search problem dressed up as a visualization.",
    approach:
      "Built agents inside Microsoft Fabric grounded on the same datasets, letting leaders ask in natural language and get a direct, source-cited response — without ever opening a report.",
    outcome:
      "Decision-making moves from ‘find the chart’ to ‘ask the question,’ giving leadership real-time access to operational signal.",
    stack: ["Microsoft Fabric", "AI Agents", "Semantic Models"],
  },
  {
    id: "reelrag",
    index: "07",
    name: "ReelRAG",
    subtitle: "Turn your short-form video library into a conversation.",
    confidential: false,
    githubUrl: "https://github.com/ingfue/Reel-RAG",
    image:
      "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    metrics: [
      { v: "Open Source", l: "MIT-licensed stack" },
      { v: "Creators", l: "& educators-first" },
    ],
    problem:
      "Creators publish hundreds of Reels, TikToks, and Shorts — but every follower question forces them to rewatch their own catalog or repeat themselves on stream.",
    approach:
      "An open-source RAG stack: upload your verticals, transcripts are indexed automatically, and your audience gets a chatbot that can recap tips, surface advice, and point to the exact clip.",
    outcome:
      "A lightweight ‘ask my channel’ experience built on what creators already produce — no extra content required.",
    stack: ["RAG", "Vector Search", "Transcription", "Open Source"],
  },
  {
    id: "research-brief",
    index: "08",
    name: "AI Research Brief Generator",
    subtitle: "From HubSpot export to executive brief, in one workflow.",
    confidential: false,
    githubUrl: "https://github.com/ingfue/AI-Research-Brief-Generator",
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    metrics: [
      { v: "FastAPI", l: "+ Azure AI Search backend" },
      { v: "Foundry", l: "agents per session" },
    ],
    problem:
      "Account teams stare at HubSpot deal exports and chat histories trying to spin a coherent research brief for every meeting. The data is rich; the synthesis is brutal.",
    approach:
      "A FastAPI backend chunks and indexes the JSON + conversation history into Azure AI Search; Azure AI Foundry agents perform native, session-scoped RAG. A React/Vite frontend supports both one-shot generation and a guided, human-in-the-loop review.",
    outcome:
      "A professional Word document brief — composed, reviewed, and ready — without the manual archeology.",
    stack: ["FastAPI", "Azure AI Search", "Azure AI Foundry", "React", "Vite"],
  },
];

export const experience = [
  {
    role: "Intermediate Enterprise System Full Stack Developer",
    company: "Okanagan College",
    location: "Kelowna, BC",
    period: "Jun 2025 — Present",
    highlights: [
      "Designed, built, and deployed 10–15 production AI agents and 15–20 automated workflows across HR, finance, compliance, audit, governance, reporting, and knowledge management — reducing manual work by ~30 hrs/week and accelerating decisions for 2,000+ staff.",
      "Built a flagship enterprise RAG-based AI agent on Azure AI Foundry that automated 80% of HR inquiries — integrated Entra ID-secured data access, MCPs, internal APIs, databases, and third-party platforms into cohesive workflows that eliminated process fragmentation.",
      "Developed specialized agents for evidence collection, control documentation, compliance reviews, document generation, ticket triage, reporting, internal Q&A, and approval workflows — codified business processes into reusable agent skills, prompts, and operating patterns for reliability and repeatability.",
      "Consolidated 7 legacy applications into a single React-based intranet for 2,000+ users with a real-time activity feed and Employee Digital Toolbox — reclaimed 48 staff-hrs/week at 99.9% availability.",
      "Implemented governed AI workflows with access boundaries, human-review checkpoints, and audit controls — partnered with technical and non-technical stakeholders to scope ambiguous processes into scalable systems with defined ownership, inputs, and success criteria.",
    ],
  },
  {
    role: "Business Analyst (Technical)",
    company: "Deloitte Global",
    location: "Toronto, ON",
    period: "May 2024 — Aug 2024",
    highlights: [
      "Directed an Agile team of 4 (Dev/QA) delivering ServiceNow CSM modules for Fortune 500 operations.",
      "Executed technical restructuring of ITSM catalogs for Deloitte’s global Store Front initiative (Chile Practice).",
      "Ran technical walkthroughs translating workflows for non-technical stakeholders.",
      "Managed UAT and JIRA defect tracking — 25% reduction in production release defects.",
    ],
  },
  {
    role: "Power Platform Developer",
    company: "New Gold Inc.",
    location: "Kamloops, BC",
    period: "Jan 2023 — Aug 2023",
    highlights: [
      "Built an industrial inspection ecosystem for 1,000+ site users — 100% mining safety compliance.",
      "Architected a KPI visualization platform across 50+ remote kiosk screens (Power BI + SQL).",
      "Integrated leave management with Workday APIs — 40% reduction in HR manual entry.",
      "Designed scalable BI data pipelines turning operational data into executive insight.",
    ],
  },
  {
    role: "CanCode Program Analyst",
    company: "Government of British Columbia",
    location: "Kamloops, BC",
    period: "Jan 2022 — Aug 2022",
    highlights: [
      "Standardized technical curriculum with modular Python / Scratch frameworks across multiple school districts.",
      "Drove a 15% increase in student participation in STEM pathways.",
      "Analyzed program performance data and reported insights that informed provincial technology programming decisions.",
      "Trained and mentored 20+ youth instructors to deliver curriculum independently across remote districts and Indigenous communities.",
    ],
  },
  {
    role: "Software Developer Intern",
    company: "Gao Tek Inc.",
    location: "New York City, NY",
    period: "Aug 2021 — Dec 2021",
    highlights: [
      "Automated regression testing suites — reduced manual validation time across release cycles.",
      "Resolved critical sprint defects under senior code review and improved cross-team documentation quality.",
      "Shipped frontend and API tickets end-to-end, learning the team's PR review and CI/CD workflow within the first sprint.",
      "Participated in daily Agile ceremonies (standups, sprint planning, retros) and contributed to estimation and grooming sessions.",
    ],
  },
];

export const skills = [
  {
    category: "Programming & Frameworks",
    items: [
      "Python (Expert)",
      "TypeScript",
      "JavaScript (ES6+)",
      "Node.js",
      "FastAPI",
      "React.js",
      "Next.js",
      "Java",
      "C#",
      "SQL",
      "Spring Boot",
      "Power Fx",
    ],
  },
  {
    category: "AI Agents & LLM Engineering",
    items: [
      "Production AI Agents",
      "LangChain",
      "LangGraph",
      "AutoGen",
      "MCP",
      "Multi-Agent Orchestration",
      "Tool-Use",
      "Agent Memory",
      "Prompt Engineering",
      "RAG",
      "OpenAI",
      "Anthropic",
      "Gemini",
      "Azure OpenAI",
      "Cursor",
      "Claude Code",
      "GitHub Copilot",
      "Codex",
      "Semantic Kernel",
      "Crew AI",
      "Lovable",
      "Replit",
      "v0",
    ],
  },
  {
    category: "RAG, Vector Search & Embeddings",
    items: [
      "Pinecone",
      "Weaviate",
      "Chroma",
      "pgvector",
      "Azure AI Search",
      "Embedding Pipelines",
      "Chunking Strategies",
      "Hybrid Search",
      "Retrieval Evaluation",
      "Document Loaders",
      "Document Parsers",
    ],
  },
  {
    category: "Workflow Automation & Integration",
    items: [
      "Power Automate",
      "Power Apps",
      "Power Pages",
      "n8n",
      "Zapier",
      "Make",
      "UiPath",
      "Azure Logic Apps",
      "REST APIs",
      "Webhooks",
      "MCP Servers",
      "ServiceNow",
      "Workday",
      "CRM Integration",
      "ERP Integration",
    ],
  },
  {
    category: "Backend & APIs",
    items: [
      "RESTful APIs",
      "GraphQL",
      "FastAPI",
      "Node.js Services",
      "Microservices",
      "Async Job Processing",
      "Message Queues",
      "Event-Driven Systems",
      "Data Modeling",
      "Schema Design",
    ],
  },
  {
    category: "Cloud, Containers & DevOps",
    items: [
      "Azure AI Foundry",
      "Azure Functions",
      "AKS",
      "App Service",
      "Document Intelligence",
      "AWS Lambda",
      "S3",
      "EKS",
      "Amazon Bedrock",
      "GCP",
      "Docker",
      "Kubernetes",
      "GitHub Actions",
      "GitLab CI/CD",
      "Terraform",
      "Entra ID",
      "Key Vault",
    ],
  },
  {
    category: "Data & Storage",
    items: [
      "PostgreSQL",
      "pgvector",
      "MySQL",
      "Azure SQL",
      "Elasticsearch",
      "Microsoft Fabric",
      "Dataverse",
      "Cosmos DB",
      "ETL Pipelines",
      "Document Processing",
      "Data Extraction",
    ],
  },
  {
    category: "Client Delivery & Methodologies",
    items: [
      "Scoping → Handoff",
      "Architecture",
      "UAT & Testing",
      "Technical Documentation",
      "Stakeholder Translation",
      "Client Training",
      "Delivery Playbooks",
      "Agile / Scrum",
      "Scrum Master",
    ],
  },
];

export const education = {
  school: "Thompson Rivers University",
  degree: "Bachelor of Computing Science — Co-op",
  period: "Jan 2021 — Apr 2025",
  location: "Kamloops, BC",
  details: [
    "Dean’s List, every semester — top 20% of program.",
    "GPA: 3.7",
  ],
};

export const certifications = [
  {
    id: "cert-azure-ai-engineer",
    name: "Microsoft Certified: AI Engineer Associate",
    url: "https://learn.microsoft.com/en-us/users/geetanshukamboj-2099/credentials/9cdc85cca2a083b6",
  },
  {
    id: "cert-power-platform-developer",
    name: "Microsoft Certified: Power Platform Developer Associate",
    url: "https://learn.microsoft.com/en-gb/users/geetanshukamboj-3421/credentials/2b4d8dfc137b4a2e",
  },
];
